/**
 * Scope dropdown fused to the left of the keyword search input.
 *
 * Options: "All" (label text + grouped suggestions) and "Label" (label text
 * only, no suggestions; expandable to pick ONE section, default "Full Label")
 * plus the field kinds when offered. Picking a section keeps All, otherwise
 * switches to Label. Purely presentational: the host owns the row's scope
 * and the section.
 */
import React, { useEffect, useRef, useState } from 'react';
import type { ChipKind, KeywordSectionOption } from '../types';
import { KIND_LABELS } from './SearchChipBar';

export type SearchScope = 'all' | 'label' | ChipKind;

// Dropdown order (the popover's group order is KIND_LABELS' own).
const KINDS: ChipKind[] = ['manufacturer', 'generic', 'brand', 'pharm_class', 'application', 'unii', 'set_id', 'dea_schedule'];

export interface SearchScopeDropdownProps {
  scope: SearchScope;
  onScopeChange: (scope: SearchScope) => void;
  /** Offer All and the field kinds (false → Label only). */
  kindsEnabled: boolean;
  sectionOptions: KeywordSectionOption[];
  /** Selected section_id, or null/'' for Full Label. */
  section: string | null;
  /** Omit where the section cannot be edited: "Label" then just switches
   *  to the Label scope. */
  onSectionChange?: ((sectionId: string | null) => void) | undefined;
  /** Letters corpus / Japanese: no section taxonomy. */
  sectionDisabled?: boolean;
  sectionDisabledTitle?: string | undefined;
  disabled?: boolean;
}

const SearchScopeDropdown: React.FC<SearchScopeDropdownProps> = ({
  scope, onScopeChange, kindsEnabled, sectionOptions, section, onSectionChange,
  sectionDisabled = false, sectionDisabledTitle, disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const opts = Array.from(
        rootRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [],
      );
      if (!opts.length) return;
      e.preventDefault();
      const i = opts.indexOf(document.activeElement as HTMLElement);
      const next = e.key === 'ArrowDown'
        ? (i + 1) % opts.length
        : (i <= 0 ? opts.length - 1 : i - 1);
      opts[next]?.focus();
    }
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const sectionLabel = section
    ? sectionOptions.find((s) => s.section_id === section)?.label
    : undefined;
  const shownSection = sectionLabel && !sectionDisabled ? sectionLabel : null;
  const triggerText = `In: ${scope === 'all'
    ? (shownSection ? `All · ${shownSection}` : 'All')
    : scope === 'label'
      ? (shownSection ?? 'Label text')
      : KIND_LABELS[scope]}`;

  const itemClass = (active: boolean) =>
    `w-full text-left px-3 py-1.5 text-sm hover:bg-accent-tint ${
      active ? 'bg-accent-tint text-accent-deep font-medium' : 'text-ink-2'
    }`;

  const pickSection = (id: string | null) => {
    onSectionChange?.(id);
    if (scope !== 'all') onScopeChange('label');
    setOpen(false);
  };

  return (
    <div ref={rootRef} onKeyDown={onKeyDown} className="relative shrink-0" data-testid="search-scope-dropdown">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Search scope"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="h-[42px] max-w-[14rem] flex items-center gap-2 px-3 bg-panel border border-line-input border-r-0 rounded-l-ctl text-[14px] text-ink hover:bg-line-subtle focus:outline-none focus:ring-2 focus:ring-accent-ring disabled:opacity-50"
      >
        <span className="truncate">{triggerText}</span>
        <svg className="w-3.5 h-3.5 shrink-0 text-ink-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Search scope options"
          className="absolute z-20 mt-1 left-0 w-64 max-h-80 overflow-y-auto bg-white border border-line rounded shadow-lg py-1"
        >
          {kindsEnabled && (
            <button
              type="button"
              role="option"
              aria-selected={scope === 'all'}
              onClick={() => { onScopeChange('all'); setOpen(false); }}
              className={itemClass(scope === 'all')}
            >
              All
            </button>
          )}
          <div className={`${itemClass(scope === 'label')} flex items-center justify-between p-0`}>
            <button
              type="button"
              role="option"
              aria-selected={scope === 'label'}
              onClick={() => {
                onScopeChange('label');
                if (onSectionChange) setExpanded(true);
              }}
              className="flex-1 text-left px-3 py-1.5"
            >
              Label
            </button>
            {onSectionChange && (
              <button
                type="button"
                data-testid="section-pill"
                aria-expanded={expanded}
                aria-label="Toggle sections"
                onClick={() => setExpanded((x) => !x)}
                className="inline-flex items-center gap-1 max-w-[9rem] mr-3 px-2 py-0.5 rounded-ctl border border-accent-ring bg-white text-xs font-semibold text-accent-deep"
              >
                <span className="truncate">
                  {sectionDisabled ? 'Full doc' : (sectionLabel ?? 'Full Label')}
                </span>
                <svg className={`w-3 h-3 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
          </div>
          {expanded && onSectionChange && (
            <div
              className="pl-3 border-l border-line-subtle ml-3"
              title={sectionDisabled ? sectionDisabledTitle : undefined}
            >
              <button
                type="button"
                role="option"
                aria-selected={!section}
                onClick={() => pickSection(null)}
                className={itemClass(!section)}
              >
                {sectionDisabled ? 'Full doc' : 'Full Label'}
              </button>
              {!sectionDisabled && sectionOptions.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  role="option"
                  aria-selected={section === s.section_id}
                  onClick={() => pickSection(s.section_id)}
                  className={itemClass(section === s.section_id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
          {kindsEnabled && KINDS.map((k) => (
            <button
              key={k}
              type="button"
              role="option"
              aria-selected={scope === k}
              onClick={() => { onScopeChange(k); setOpen(false); }}
              className={itemClass(scope === k)}
            >
              {KIND_LABELS[k]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchScopeDropdown;
